import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Check, Loader2, X, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [planId, setPlanId] = useState('');
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) { setStatus('timeout'); return; }
      attempts++;
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`, { credentials: 'include' });
        const data = await res.json();
        setPlanId(data.plan_id);
        if (data.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
          // If we're the checkout popup, close ourselves — the main tab polls and unlocks
          setTimeout(() => { try { window.close(); } catch {} }, 1500);
          return;
        }
        if (data.status === 'expired') { setStatus('expired'); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-6">
        {status === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto" />
            <h2 className="text-xl font-semibold">Confirming your payment...</h2>
            <p className="text-muted-foreground">This usually takes a few seconds.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold">Welcome to Pro!</h2>
            <p className="text-muted-foreground">
              {planId === 'lifetime' ? "You now have lifetime unlimited access." : "Your monthly Pro subscription is active."}
              {' '}Enjoy unlimited downloads and all premium features.
            </p>
            <Button onClick={() => navigate('/editor')} className="mt-4">
              Start Editing
            </Button>
          </>
        )}
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <X className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold">
              {status === 'expired' ? 'Payment session expired' : 'Something went wrong'}
            </h2>
            <p className="text-muted-foreground">Please try again or contact support.</p>
            <Link to="/pricing">
              <Button variant="outline" className="mt-4">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Pricing
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
